import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usersApi } from '../../api/client';
import { FeedCard } from '../../components/FeedCard/FeedCard';
import { Spot } from '../../types';
import { spotApiToSpot } from '../../context/DataContext';
import styles from '../Favorites/Favorites.module.css';

type Category = 'all' | 'liked' | 'beautiful' | 'cool' | 'sweetPhoto';

const CATEGORIES: { key: Category; label: string; icon: string }[] = [
  { key: 'all', label: 'All', icon: '★' },
  { key: 'liked', label: 'Liked', icon: '♥' },
  { key: 'beautiful', label: 'Beautiful', icon: '✨' },
  { key: 'cool', label: 'Cool', icon: '😎' },
  { key: 'sweetPhoto', label: 'Sweet Photo', icon: '📷' },
];

export function MyFavorites() {
  const { user, isAuthenticated } = useAuth();
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [collections, setCollections] = useState<{
    all: Spot[];
    liked: Spot[];
    beautiful: Spot[];
    cool: Spot[];
    sweetPhoto: Spot[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCollections = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setCollections({
        all: [],
        liked: [],
        beautiful: [],
        cool: [],
        sweetPhoto: [],
      });
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const myCollections = await usersApi.getMyCollections();
      const liked = myCollections.liked.map(spotApiToSpot);
      const beautiful = myCollections.beautiful.map(spotApiToSpot);
      const cool = myCollections.cool.map(spotApiToSpot);
      const sweetPhoto = myCollections.sweetPhoto.map(spotApiToSpot);
      const all = Array.from(
        new Map(
          [...liked, ...beautiful, ...cool, ...sweetPhoto]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((spot) => [spot.id, spot])
        ).values()
      );
      setCollections({
        all,
        liked,
        beautiful,
        cool,
        sweetPhoto,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load your favorites');
      setCollections({
        all: [],
        liked: [],
        beautiful: [],
        cool: [],
        sweetPhoto: [],
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const spots = collections ? collections[activeCategory] : [];
  const counts = collections
    ? {
        all: collections.all.length,
        liked: collections.liked.length,
        beautiful: collections.beautiful.length,
        cool: collections.cool.length,
        sweetPhoto: collections.sweetPhoto.length,
      }
    : null;

  return (
    <div className={styles.page}>
      <h1>My Favorites</h1>
      <p className={styles.subtitle}>
        See every spot you personally tagged, across liked, beautiful, cool, and sweet photo
      </p>

      {!isAuthenticated || !user ? (
        <>
          <p className={styles.signInPrompt}>
            Sign in to view the spots you have tagged.
          </p>
          <Link to="/login" className={styles.cta}>Sign in</Link>
        </>
      ) : loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          <div className={styles.tabs}>
            {CATEGORIES.map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                className={activeCategory === key ? styles.tabActive : styles.tab}
                onClick={() => setActiveCategory(key)}
              >
                {icon} {label}
                {counts != null && <span className={styles.tabCount}> ({counts[key]})</span>}
              </button>
            ))}
          </div>

          <div className={styles.feed}>
            {spots.length === 0 ? (
              <div className={styles.empty}>
                <p>
                  {activeCategory === 'all' && 'No tagged spots yet. React to posts from the feed to see them here.'}
                  {activeCategory === 'liked' && 'No liked spots yet. Like posts from the feed to see them here.'}
                  {activeCategory === 'beautiful' && 'No “beautiful” spots yet. Mark posts as beautiful from the feed.'}
                  {activeCategory === 'cool' && 'No “cool” spots yet. Mark posts as cool from the feed.'}
                  {activeCategory === 'sweetPhoto' && 'No “sweet photo” spots yet. Mark posts as sweet photo from the feed.'}
                </p>
              </div>
            ) : (
              spots.map((spot) => <FeedCard key={spot.id} spot={spot} compact />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
